import { createWebHistory, createRouter } from 'vue-router';
import axios from 'axios';
import routes from './routes';
import appConfig from '../../app.config';
import store from '@/shared/state/store';
import authService from '@/shared/services/auth.service';
import { ROLES } from '@/constants/roles';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

router.beforeEach(async (routeTo, routeFrom, next) => {
  // /unauthorized must be public: the requiresValidResetCode guard below sends
  // visitors there, and they are signed out by definition when they arrive
  // from a stale password link.
  const publicPages = [
    '/login',
    '/forgot-password',
    '/unauthorized',
    '/web-app',
  ];
  const isPublicPage =
    publicPages.includes(routeTo.path) ||
    routeTo.path.startsWith('/rts/') ||
    routeTo.path.startsWith('/web-app/');

  // Allow public routes without checks
  if (isPublicPage) return next();

  if (routeTo.meta.requiresValidResetCode) {
    const code = routeTo.query.code;
    // A missing code and a rejected one are the same failure to the visitor:
    // the link they followed is no good.
    if (!code) return next({ path: '/unauthorized', query: { invalid: 1 } });

    try {
      await authService.validateCode(code);
      return next();
    } catch (err) {
      return next({ path: '/unauthorized', query: { invalid: 1 } });
    }
  }

  // Restore session from localStorage if not already done
  if (!store.getters['auth/sessionRestored']) {
    store.dispatch('auth/restoreSession');
  }

  // Check authentication
  const isAuthenticated = store.getters['auth/isAuthenticated'];
  const jwt = store.getters['auth/accessToken'];

  // Redirect to login if not authenticated
  if (!isAuthenticated || !jwt) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // Set authorization header
  if (jwt) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`;
  }

  /*
    Un mot de passe temporaire n'ouvre qu'une porte: celle qui le remplace.
    L'API le fait respecter de son cote (TemporaryPasswordGuard); ici, c'est
    pour que le compte arrive sur l'ecran qui le debloque plutot que sur une
    page qui echouerait en 403.
  */
  const mustChangePassword = store.getters['auth/mustChangePassword'];

  if (mustChangePassword && routeTo.name !== 'change-password') {
    return next({ name: 'change-password' });
  }

  // L'inverse: l'ecran n'a plus d'objet une fois le mot de passe choisi.
  if (!mustChangePassword && routeTo.name === 'change-password') {
    return next({ path: '/' });
  }

  // Check role-based access control (RBAC)
  const userRole = store.getters['auth/userRole'];
  const requiredRoles = routeTo.meta?.roles;
  const isSuperAdmin = userRole === 'SuperAdmin';
  // If route has roles restriction, check if user has required role
  if (
    requiredRoles &&
    Array.isArray(requiredRoles) &&
    requiredRoles.length > 0
  ) {
    if (!isSuperAdmin && (!userRole || !requiredRoles.includes(userRole))) {
      // User doesn't have required role, redirect to unauthorized
      return next({ name: 'unauthorized' });
    }
  }
  if (userRole === ROLES.PROFESSIONAL) {
    const isRootOpen =
      routeTo.path === '/' || routeTo.name === 'Accueil' || routeTo.path === '';
    if (isRootOpen) {
      return next({ name: 'my-surveys' });
    }
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
